import { type FC, useEffect } from "react";

import { isAfter } from "date-fns";

import { useCofConfigs, useLogAbExposure } from "../../cof-feature-gates/hooks";
import { type CofConfigId } from "../../gql-generated/types";
import { isProd } from "../constants";
import { parseBannerConfig } from "../models";

import { Banner, type BannerDismissProps } from "./Banner";

type BannerContainerProps = {
    configId: CofConfigId;
    bannerContainerClassName?: string;
    webpImageUrl?: string;
    jpegImageUrl?: string;
    svgImageUrl?: string;
    isInverted?: boolean;
    dataTestId?: string;
} & BannerDismissProps;

export const BannerContainer: FC<BannerContainerProps> = ({
    bannerContainerClassName,
    configId,
    webpImageUrl,
    jpegImageUrl,
    svgImageUrl,
    isInverted,
    dataTestId,
    onDismiss,
    dismissLabel,
}) => {
    const { configs, loading: configsLoading, error: configsError } = useCofConfigs();

    const bannerConfig = configs[configId];

    const logExposure = useLogAbExposure();

    useEffect(() => {
        if (!isProd() || !bannerConfig.shouldLogExposure) {
            return;
        }

        const { studyName, experimentId } = bannerConfig;
        void logExposure({ studyName, treatmentId: experimentId });
    }, [bannerConfig, logExposure]);

    const parsedBannerConfig = parseBannerConfig(bannerConfig.value?.stringValue ?? "");

    if (configsLoading || !!configsError || !parsedBannerConfig) {
        return null;
    }

    const { titleText, subtitleText, ctaText, ctaURL, openInNewTab, expiryDate } = parsedBannerConfig;

    const hasExpired = expiryDate ? isAfter(new Date(), new Date(expiryDate)) : false;

    if (hasExpired) {
        return null;
    }

    const dismissProps: BannerDismissProps = onDismiss && dismissLabel ? { onDismiss, dismissLabel } : {};

    return (
        <Banner
            {...dismissProps}
            dataTestId={dataTestId}
            subtitleText={subtitleText}
            openInNewTab={openInNewTab}
            titleText={titleText}
            ctaText={ctaText}
            ctaURL={ctaURL}
            bannerContainerClassName={bannerContainerClassName}
            webpImageUrl={webpImageUrl}
            jpegImageUrl={jpegImageUrl}
            svgImageUrl={svgImageUrl}
            isInverted={isInverted}
        />
    );
};
